import React, { useRef, useState } from 'react';
import { uploadImage } from '../api';

/**
 * CoverUpload — reusable clan-cover picker.
 *
 * Props:
 *   value        — current cover_url (string | null)
 *   onChange     — fn(newUrl) called after a successful upload OR removal
 *   onError      — fn(message) called on validation / upload failure
 *   disabled     — bool, optional
 *   testIdPrefix — string, optional; used to scope data-testid attrs
 *
 * Behaviour:
 *   - Accepts JPG / PNG / WEBP up to 5 MB
 *   - Shows a preview when a cover exists
 *   - Uploads to POST /api/files/upload (existing endpoint) and returns the
 *     resulting path via `onChange`. Parent is responsible for persisting the
 *     path onto the clan record (e.g. PATCH /api/clans/mine {cover_url: ...}).
 */
const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

export default function CoverUpload({
  value,
  onChange,
  onError,
  disabled = false,
  testIdPrefix = 'cover-upload',
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const upload = async (file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      onError?.('Please pick a JPG, PNG, or WEBP image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      onError?.('Image is too large (max 5 MB).');
      return;
    }
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      onChange?.(url);
    } catch (ex) {
      onError?.(ex?.response?.data?.detail || ex?.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    upload(f);
  };

  const clear = () => onChange?.(null);

  return (
    <div className="cover-upload" data-testid={testIdPrefix}>
      {value ? (
        <div className="cover-upload-preview">
          <img
            src={value}
            alt="Clan cover preview"
            data-testid={`${testIdPrefix}-preview`}
          />
          <div className="cover-upload-preview-actions">
            <button
              type="button"
              className="cover-upload-btn"
              onClick={pick}
              disabled={disabled || uploading}
              data-testid={`${testIdPrefix}-replace`}
            >
              {uploading ? 'Uploading…' : 'Replace'}
            </button>
            <button
              type="button"
              className="cover-upload-btn cover-upload-btn-remove"
              onClick={clear}
              disabled={disabled || uploading}
              data-testid={`${testIdPrefix}-remove`}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="cover-upload-drop"
          onClick={pick}
          disabled={disabled || uploading}
          data-testid={`${testIdPrefix}-pick`}
        >
          <div className="cover-upload-icon">🖼️</div>
          <div className="cover-upload-title">
            {uploading ? 'Uploading…' : 'Add a cover picture (optional)'}
          </div>
          <div className="cover-upload-hint">
            Recommended 1500 × 500 px · Max 5 MB · JPG, PNG or WEBP
          </div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        onChange={onFile}
        style={{ display: 'none' }}
        data-testid={`${testIdPrefix}-input`}
      />
    </div>
  );
}
